/**
 * EndpointManager — API/WebSocket 多端點故障轉移管理器
 *
 * 支援多層備援端點（主要 → 備援1 → 備援2 ...），逐級降級。
 * 定期探測更高優先級端點，恢復後自動切回。
 */

interface Endpoint {
  name: string;
  api: string;
  ws: string;
}

// --- 端點定義 ---

const PRIMARY_WS = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

function originToWs(origin: string): string {
  return origin.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
}

const FAILOVER1 = process.env.REACT_APP_FAILOVER_URL || '';
const FAILOVER2 = process.env.REACT_APP_FAILOVER2_URL || '';

// 建立端點陣列（僅加入有設定的）
const _endpoints: Endpoint[] = [
  { name: '主要', api: '', ws: PRIMARY_WS },
  ...(FAILOVER1 ? [{ name: '備援1', api: FAILOVER1, ws: originToWs(FAILOVER1) }] : []),
  ...(FAILOVER2 ? [{ name: '備援2', api: FAILOVER2, ws: originToWs(FAILOVER2) }] : []),
];

let _currentIndex = 0;
let _probeTimer: ReturnType<typeof setInterval> | null = null;

// --- 狀態查詢 ---

/** 是否有設定備援端點 */
export function hasFallback(): boolean {
  return _endpoints.length > 1;
}

/** 目前是否使用備援端點 */
export function isUsingFallback(): boolean {
  return _currentIndex > 0;
}

/** 是否已在最低層備援（無法再降級） */
export function isLastFallback(): boolean {
  return _currentIndex >= _endpoints.length - 1;
}

/** 取得目前端點資訊（供 UI 或除錯） */
export function getCurrentEndpointInfo(): { name: string; index: number; total: number } {
  return {
    name: _endpoints[_currentIndex].name,
    index: _currentIndex,
    total: _endpoints.length,
  };
}

// --- 端點取得 ---

/** 取得目前 API baseURL */
export function getApiBase(): string {
  return _endpoints[_currentIndex].api;
}

/** 取得目前 WS baseURL */
export function getWsBase(): string {
  return _endpoints[_currentIndex].ws;
}

// --- 切換邏輯 ---

/** 切換到下一層備援端點，啟動探測 */
export function switchToNextFallback(): void {
  if (_currentIndex >= _endpoints.length - 1) return;
  _currentIndex++;
  const ep = _endpoints[_currentIndex];
  console.warn('[EndpointManager] 切換到%s: %s', ep.name, ep.api || '(same-origin)');
  startProbing();
}

/** 向下相容別名 */
export const switchToFallback = switchToNextFallback;

/** 切換回主要端點 */
export function switchToPrimary(): void {
  if (_currentIndex === 0) return;
  _currentIndex = 0;
  console.info('[EndpointManager] 切回主要端點');
  stopProbing();
}

// --- 健康檢查與探測 ---

/** 健康檢查（用 fetch 避免觸發 axios 攔截器遞迴） */
async function healthCheck(apiBase: string): Promise<boolean> {
  try {
    const resp = await fetch(`${apiBase}/api/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

/** 定期探測更高優先級端點（30 秒），恢復後自動切回 */
function startProbing(): void {
  if (_probeTimer) return;
  _probeTimer = setInterval(async () => {
    // 從最高優先級（主端點）開始逐層探測
    for (let i = 0; i < _currentIndex; i++) {
      const ok = await healthCheck(_endpoints[i].api);
      if (ok) {
        const restored = _endpoints[i];
        _currentIndex = i;
        console.info('[EndpointManager] %s恢復，切回（層級 %d）', restored.name, i);
        if (i === 0) stopProbing();
        window.dispatchEvent(new CustomEvent('endpoint-restored'));
        return;
      }
    }
  }, 30_000);
}

function stopProbing(): void {
  if (_probeTimer) {
    clearInterval(_probeTimer);
    _probeTimer = null;
  }
}
